import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import cx from 'classnames';
import { MDText } from 'i18n-react';
import Papa from 'papaparse';

import { DownloadCSV } from 'Components/DownloadCSV/DownloadCSV';
import { pollingPropsDefaults } from 'Components/DownloadCSV/DownloadCSV.consts';
import type { VehicleListItem } from 'Types/vehicle';

import { getVehiclesCount } from '../../VehicleList.helpers';
import { VehicleCardV2 } from '../VehicleCardV2/VehicleCardV2';
import { VehicleGridV2 } from '../VehicleGridV2/VehicleGridV2';

import texts from '../../VehicleListError/VehicleListError.texts';

import styles from './VehicleListErrorV2.module.scss';

const T = new MDText(texts);
const translate = (text: string) => String(T.translate(text));

type CsvRow = Record<string, string>;

const CSV_REPORT_URL = '/api/vehicle/report';

const VEHICLES_PER_PAGE = 50;

const parsePrice = (raw: string): number | undefined => {
	const n = Number(raw?.replaceAll(/[^\d.]/g, ''));
	return n > 0 ? n : undefined;
};

const csvRowToVehicle = (row: CsvRow) => {
	const idMatch = /\/vehicles\/(\d+)/.exec(row['Motorway vehicle link'] ?? '');
	return {
		displayPrice: parsePrice(row['Reserve price']),
		distance: undefined,
		expiryDate: undefined,
		fuel: row.Fuel || '',
		highestBid: undefined,
		id: idMatch ? Number(idMatch[1]) : 0,
		isPaidListing: null,
		isTrade: false,
		isVatQualifying: false,
		leadPhoto: {},
		make: row.Make || '',
		mileage: Number(row.Mileage) || 0,
		model: row.Model || '',
		numericGrade: row['Exterior grade'] || null,
		originalPrice: undefined,
		prettyVrm: row.VRM || '',
		reheatCount: undefined,
		state: {
			category: 'for_sale',
			id: 0,
			name: 'Live sale',
			slug: 'sale_active',
		},
		transmission: row.Transmission || '',
		year: Number(row.Year) || 0,
	} as unknown as VehicleListItem;
};

interface VehicleListErrorV2Props {
	errorMessage?: string;
	errorTitle?: string;
}

export const VehicleListErrorV2: React.FC<VehicleListErrorV2Props> = ({ errorMessage, errorTitle }) => {
	const [vehicles, setVehicles] = useState<CsvRow[]>([]);
	const [hasCsvFailed, setHasCsvFailed] = useState(false);
	const [visibleCount, setVisibleCount] = useState(VEHICLES_PER_PAGE);
	const sentinelRef = useRef<HTMLDivElement>(null);

	const loadMore = useCallback(() => {
		setVisibleCount((prev) => prev + VEHICLES_PER_PAGE);
	}, []);

	useEffect(() => {
		const el = sentinelRef.current;
		if (!el) return undefined;

		const observer = new IntersectionObserver(([entry]) => {
			if (entry.isIntersecting) loadMore();
		});
		observer.observe(el);

		return () => observer.disconnect();
	}, [loadMore, vehicles.length]);

	useEffect(() => {
		const fetchCsv = async () => {
			try {
				const { data } = await axios.create().get(CSV_REPORT_URL, { responseType: 'text' });
				const { data: rows } = Papa.parse<CsvRow>(typeof data === 'string' ? data : String(data), {
					header: true,
					skipEmptyLines: true,
				});
				setVehicles(rows);
				if (rows.length === 0) setHasCsvFailed(true);
			} catch {
				setHasCsvFailed(true);
			}
		};
		fetchCsv().catch(() => {
			// swallow fetch errors — we show an error message in the UI
		});
	}, []);

	return (
		<div className={styles.container}>
			<div className={styles.banner}>
				<h2 className={styles.bannerHeading}>{errorTitle || translate('heading')}</h2>
				<p
					data-thc-style-override
					className={cx(styles.bannerContent, {
						[styles.errorWithoutDownload]: hasCsvFailed,
					})}
				>
					{errorMessage || T.translate(hasCsvFailed ? 'contentNoVehicles' : 'content')}
				</p>
				{!hasCsvFailed && (
					<div className={styles.actions}>
						<DownloadCSV
							downloadButtonLabel={translate('downloadCsv')}
							pollingProps={{ ...pollingPropsDefaults, filenameTitle: 'Motorway vehicles' }}
							staticDownloadUrl={CSV_REPORT_URL}
						/>
					</div>
				)}
			</div>

			{vehicles.length > 0 && (
				<p data-thc-style-override className={styles.vehicleCount}>
					{getVehiclesCount('vehicleList.showingVehicle', vehicles.length)}
				</p>
			)}

			{vehicles.length > 0 && (
				<VehicleGridV2>
					{vehicles.slice(0, visibleCount).map((row) => {
						const vehicle = csvRowToVehicle(row);
						if (!vehicle.id) return null;
						return <VehicleCardV2 hideShortlist key={vehicle.id} vehicle={vehicle} />;
					})}
				</VehicleGridV2>
			)}

			{visibleCount < vehicles.length && <div ref={sentinelRef} className={styles.sentinel} />}
		</div>
	);
};
